import React, { useEffect, useState } from 'react';
import { Smartphone, Share, Plus, X, Download } from 'lucide-react';
import { Button } from './ui/button';

const DISMISS_KEY = 'tz_install_dismissed';
const DISMISS_DAYS = 7;

const isIOS = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const isAndroid = () => /Android/i.test(navigator.userAgent);

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || window.innerWidth < 768;
};

const wasRecentlyDismissed = () => {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const days = (Date.now() - parseInt(ts, 10)) / (1000 * 60 * 60 * 24);
    return days < DISMISS_DAYS;
  } catch (_e) {
    return false;
  }
};

const InstallPrompt = () => {
  const [show, setShow] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (isStandalone() || !isMobile() || wasRecentlyDismissed()) return undefined;

    // Android / Chrome: capture the install event
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS doesn't fire the event - show after small delay
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => setShow(true), 2500);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        await deferredPrompt.userChoice;
      } catch (err) {
        console.warn('[InstallPrompt] userChoice rejected', err);
      }
      setDeferredPrompt(null);
      setShow(false);
    } else if (isIOS()) {
      setShowIOSGuide(true);
    }
  };

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (err) {
      console.warn('[InstallPrompt] could not persist dismiss state', err);
    }
    setShow(false);
    setShowIOSGuide(false);
  };

  if (!show && !showIOSGuide) return null;

  // iOS detailed guide
  if (showIOSGuide) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:hidden">
        <div className="mx-auto max-w-md overflow-hidden rounded-2xl border border-emerald-200 bg-white shadow-2xl">
          <div className="flex items-start justify-between gap-3 border-b border-slate-100 bg-emerald-50 px-4 py-3">
            <div className="flex items-center gap-2">
              <img src="/isotipo.png" alt="" className="h-9 w-9 rounded-lg" />
              <div>
                <p className="text-sm font-semibold text-slate-900">Instalar en tu iPhone</p>
                <p className="text-xs text-slate-600">Sigue estos 3 pasos</p>
              </div>
            </div>
            <button onClick={dismiss} className="rounded-full p-1 text-slate-500 hover:bg-white hover:text-slate-700" aria-label="Cerrar">
              <X className="h-4 w-4" />
            </button>
          </div>
          <ol className="space-y-3 px-4 py-4 text-sm text-slate-700">
            <li className="flex items-center gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 font-bold text-emerald-800">1</span>
              <span>Toca el botón <Share className="inline h-4 w-4 align-text-bottom text-emerald-700" /> <strong>Compartir</strong> en la barra inferior de Safari.</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 font-bold text-emerald-800">2</span>
              <span>Desplázate y selecciona <Plus className="inline h-4 w-4 align-text-bottom text-emerald-700" /> <strong>“Agregar a pantalla de inicio”</strong>.</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 font-bold text-emerald-800">3</span>
              <span>Toca <strong>Agregar</strong> en la esquina superior derecha. ¡Listo!</span>
            </li>
          </ol>
          <div className="border-t border-slate-100 px-4 py-3">
            <Button onClick={dismiss} variant="outline" className="h-10 w-full">Entendido</Button>
          </div>
        </div>
      </div>
    );
  }

  // Generic install banner (Android shows native prompt, iOS triggers guide)
  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:hidden">
      <div className="mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-emerald-200 bg-white p-3 shadow-2xl">
        <img src="/isotipo.png" alt="" className="h-12 w-12 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">Instala la app</p>
          <p className="text-xs text-slate-600">Acceso rápido desde tu pantalla de inicio.</p>
        </div>
        <Button
          onClick={handleInstall}
          className="h-10 shrink-0 gap-1.5 bg-emerald-700 px-3 text-xs text-white hover:bg-emerald-800"
        >
          {isIOS() ? <Smartphone className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" />}
          Instalar
        </Button>
        <button onClick={dismiss} className="shrink-0 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Cerrar">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
